import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, errMsg, short, usd, signedUsd, tone } from "@/lib/api";
import { SourcesForm } from "@/components/SourcesForm";

const Stat = ({ label, value, testid }) => (
  <div className="panel p-5" data-testid={testid}>
    <div className="panel-title">{label}</div>
    <div className="mt-3 font-mono text-2xl font-bold">{value}</div>
  </div>
);

const ConfigForm = ({ config, reload }) => {
  const [form, setForm] = useState({ treasury_wallet: config.treasury_wallet || "", payout_wallet: config.payout_wallet || "" });
  const save = async () => {
    try {
      await api.post("/admin/config", form);
      toast.success("Saved");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const field = (k, label, hint) => (
    <label className="block">
      <span className="text-xs text-slate-300">{label}</span>
      <input data-testid={`config-${k}-input`} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} placeholder="Solana address"
        className="mt-1 w-full bg-[#07090D] border border-[#1E293B] px-3 py-2 text-xs font-mono outline-none focus:border-[#14F195]/60 transition-colors" />
      <span className="text-[10px] text-slate-500">{hint}</span>
    </label>
  );
  return (
    <div className="panel p-5 space-y-4" data-testid="owner-config">
      <div className="panel-title">Payments config</div>
      {field("treasury_wallet", "Treasury wallet (receives $9.99 subscriptions)", "Subscriptions stay closed until this is set.")}
      {field("payout_wallet", "Your payout wallet (bot withdrawals)", "Used when you withdraw your own bot wallet without entering an address.")}
      <button data-testid="config-save-btn" onClick={save} className="px-4 py-2 bg-[#14F195] text-[#07090D] text-xs font-bold hover:bg-[#0fd884] transition-colors">Save</button>
    </div>
  );
};

const UsersTable = ({ users, reload }) => {
  const act = async (path, body, msg) => {
    try {
      await api.post(path, body);
      toast.success(msg);
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="panel" data-testid="owner-users-table">
      <div className="px-5 py-4 border-b border-[#1E293B] panel-title">Subscribers</div>
      <div className="overflow-auto scroll-thin">
        <table className="w-full text-xs font-mono min-w-[900px]">
          <thead className="text-slate-500 text-[10px] uppercase tracking-wider">
            <tr className="border-b border-[#1E293B]">
              {["Account", "Status", "Expires", "Mode", "Size", "Trades", "PnL", "Joined", ""].map((h) => <th key={h} className="px-4 py-2.5 text-left font-medium">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {users.length === 0 && <tr><td colSpan={9} className="px-5 py-10 text-center text-slate-500" data-testid="owner-users-empty">No subscribers yet</td></tr>}
            {users.map((x) => (
              <tr key={x.id} className="border-b border-[#1E293B]/60 hover:bg-[#111827] transition-colors" data-testid={`owner-user-row-${x.id}`}>
                <td className="px-4 py-2.5">{x.email || short(x.wallet)}</td>
                <td className="px-4 py-2.5">{x.active ? <span className="text-[#14F195]">ACTIVE</span> : <span className="text-slate-500">INACTIVE</span>}</td>
                <td className="px-4 py-2.5 text-slate-400">{x.sub_expires ? new Date(x.sub_expires).toLocaleDateString() : "—"}</td>
                <td className="px-4 py-2.5 text-slate-400">{x.mode}{x.running ? "" : " · paused"}</td>
                <td className="px-4 py-2.5">{usd(x.trade_usd)}</td>
                <td className="px-4 py-2.5">{x.trades}</td>
                <td className={`px-4 py-2.5 ${tone(x.pnl)}`}>{signedUsd(x.pnl)}</td>
                <td className="px-4 py-2.5 text-slate-500">{new Date(x.created_at).toLocaleDateString()}</td>
                <td className="px-4 py-2.5 text-right whitespace-nowrap">
                  <button data-testid={`owner-grant-${x.id}`} onClick={() => act(`/admin/users/${x.id}/grant`, { days: 30 }, "Added 30 days")}
                    className="px-2 py-1 border border-[#14F195]/40 text-[#14F195] hover:bg-[#14F195]/10 transition-colors">+30d</button>
                  <button data-testid={`owner-revoke-${x.id}`} onClick={() => act(`/admin/users/${x.id}/revoke`, {}, "Access ended")}
                    className="ml-2 px-2 py-1 border border-[#EF4444]/40 text-[#EF4444] hover:bg-[#EF4444]/10 transition-colors">Revoke</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const Payments = ({ payments }) => (
  <div className="panel" data-testid="owner-payments">
    <div className="px-5 py-4 border-b border-[#1E293B] panel-title">Recent payments</div>
    <div className="max-h-[300px] overflow-auto scroll-thin font-mono text-xs">
      {payments.length === 0 && <div className="px-5 py-8 text-center text-slate-500">No payments yet</div>}
      {payments.map((p) => (
        <div key={p.id} className="flex justify-between px-5 py-2 border-b border-[#1E293B]/60">
          <span>{short(p.wallet)}</span>
          <span className="text-[#14F195]">{p.sol.toFixed(4)} SOL · {usd(p.usd)}</span>
          <a href={`https://solscan.io/tx/${p.signature}`} target="_blank" rel="noreferrer" className="text-slate-500 hover:text-white">{new Date(p.created_at).toLocaleDateString()}</a>
        </div>
      ))}
    </div>
  </div>
);

export default function OwnerPanel() {
  const [data, setData] = useState(null);
  const reload = useCallback(() => api.get("/admin/overview").then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => { reload(); }, [reload]);
  if (!data) return <div className="py-20 text-center font-mono text-sm text-slate-500">Loading…</div>;
  const r = data.revenue;
  return (
    <div className="space-y-4 rise" data-testid="owner-panel">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat testid="owner-active-subs" label="Active subscribers" value={`${r.active_subscribers} / ${r.total_users}`} />
        <Stat testid="owner-mrr" label="Est. monthly revenue" value={usd(r.active_subscribers * data.config.sub_price_usd)} />
        <Stat testid="owner-revenue-30d" label="Revenue · 30 days" value={usd(r.last_30d_usd)} />
        <Stat testid="owner-revenue-total" label="Revenue · all time" value={`${usd(r.total_usd)} · ${r.total_sol.toFixed(3)} SOL`} />
      </div>
      <div className="grid xl:grid-cols-12 gap-4">
        <div className="xl:col-span-8"><UsersTable users={data.users} reload={reload} /></div>
        <div className="xl:col-span-4 space-y-4">
          <ConfigForm key={`${data.config.treasury_wallet}-${data.config.payout_wallet}`} config={data.config} reload={reload} />
          <SourcesForm sources={data.sources} reload={reload} />
          <Payments payments={data.payments} />
        </div>
      </div>
    </div>
  );
}
